import { AxiosError, AxiosInstance, AxiosResponse } from "axios";

/**
 * Phase 0 instrumentation: counts HTTP requests per endpoint and per screen so
 * the optimization program has a baseline to compare against. See
 * `docs/2026-09-03-plan-optimizacion-requests-renders.md`.
 *
 * Inert unless REACT_APP_REQUEST_COUNTER === "true", which only
 * .env.development sets. The other env files declare it as "false" rather than
 * leaving it out: CRA only substitutes REACT_APP_* vars it actually finds, so
 * an absent flag stays an unresolved runtime lookup and the minifier ships the
 * whole counter as dead code instead of dropping it.
 *
 * Read the results from the browser console:
 *   __innovusRequests.table()    printed summary, worst offender first
 *   __innovusRequests.dump()     downloads the full JSON, screens included
 *   __innovusRequests.reset()    clears the counters
 */

const ENABLED = process.env.REACT_APP_REQUEST_COUNTER === "true";
const STORAGE_KEY = "innovus:requestCounts";
const FLUSH_INTERVAL_MS = 2000;

export interface EndpointStat {
  count: number;
  errors: number;
  totalMs: number;
  /**
   * Aborted through an AbortSignal (T3). Optional because state persisted
   * before this field existed is rehydrated from localStorage without it.
   */
  canceled?: number;
}

/**
 * Mean latency over the requests that actually completed. A cancelled request
 * contributes nothing to totalMs, so counting it in the divisor would report a
 * latency lower than any request ever took.
 */
export const meanMs = (stat: EndpointStat): number => {
  const completed = stat.count - (stat.canceled ?? 0);
  return completed ? Math.round(stat.totalMs / completed) : 0;
};

/**
 * The endpoints map as the dump writes it: the raw counters, which are what
 * lets a later measurement be recomputed the same way, plus the mean, because
 * the file is read by hand and dividing totalMs by count by eye is where the
 * mistakes creep in.
 *
 * Ordered by call count, most called first, same as the console table. JSON
 * objects carry no ordering guarantee in the format, but JSON.stringify writes
 * string keys in insertion order and every editor shows them that way, so the
 * file opens on the worst offender instead of on whatever was requested first.
 * These keys are never integer-like ("GET /v2/Shipment"), which is the one
 * case where a JavaScript object would reorder them behind our back.
 */
export const endpointsWithMean = (
  endpoints: Record<string, EndpointStat>
): Record<string, EndpointStat & { avgMs: number }> => {
  const out: Record<string, EndpointStat & { avgMs: number }> = {};
  Object.keys(endpoints)
    .sort((a, b) => endpoints[b].count - endpoints[a].count)
    .forEach((endpoint) => {
      const stat = endpoints[endpoint];
      out[endpoint] = { ...stat, avgMs: meanMs(stat) };
    });
  return out;
};

interface CounterState {
  startedAt: string;
  totalRequests: number;
  endpoints: Record<string, EndpointStat>;
  /** Endpoint counts broken down by the route the user was on. */
  screens: Record<string, Record<string, number>>;
}

const emptyState = (): CounterState => ({
  startedAt: new Date().toISOString(),
  totalRequests: 0,
  endpoints: {},
  screens: {},
});

const load = (): CounterState => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as CounterState;
    if (!parsed?.endpoints || !parsed?.screens) return emptyState();
    return parsed;
  } catch {
    return emptyState();
  }
};

let state: CounterState = ENABLED ? load() : emptyState();
let dirty = false;

const flush = () => {
  if (!dirty) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    dirty = false;
  } catch {
    // Storage full or blocked. Counting continues in memory.
  }
};

const GUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Collapses the volatile parts of a URL so the same endpoint aggregates into
 * one row: origin and query string dropped, ids replaced by `:id`. Without
 * this a table of "top URLs" is one row per shipment number and tells you
 * nothing.
 */
export const normalizeUrl = (url?: string): string => {
  if (!url) return "(sin url)";

  let path = url;
  const schemeEnd = path.indexOf("://");
  if (schemeEnd !== -1) {
    const afterScheme = path.slice(schemeEnd + 3);
    const firstSlash = afterScheme.indexOf("/");
    path = firstSlash === -1 ? "/" : afterScheme.slice(firstSlash);
  }

  const queryStart = path.indexOf("?");
  if (queryStart !== -1) path = path.slice(0, queryStart);

  return (
    path
      .split("/")
      .map((segment) => {
        if (!segment) return segment;
        if (GUID.test(segment)) return ":id";
        if (/^\d+$/.test(segment)) return ":id";
        return segment;
      })
      .join("/") || "/"
  );
};

const currentScreen = (): string => {
  try {
    return window.location.pathname || "/";
  } catch {
    return "/";
  }
};

const record = (
  method: string,
  url: string | undefined,
  elapsedMs: number,
  failed: boolean,
  canceled = false
) => {
  const key = `${method.toUpperCase()} ${normalizeUrl(url)}`;

  const stat = state.endpoints[key] ?? { count: 0, errors: 0, totalMs: 0 };
  stat.count += 1;
  if (canceled) {
    // Aborted requests count as traffic, but neither as failures nor towards
    // the mean latency: most of them never reached the network.
    stat.canceled = (stat.canceled ?? 0) + 1;
  } else {
    stat.totalMs += elapsedMs;
    if (failed) stat.errors += 1;
  }
  state.endpoints[key] = stat;

  const screen = currentScreen();
  const byScreen = state.screens[screen] ?? {};
  byScreen[key] = (byScreen[key] ?? 0) + 1;
  state.screens[screen] = byScreen;

  state.totalRequests += 1;
  dirty = true;
};

/** Rows sorted by call count, which is the number this program optimizes. */
const rows = () =>
  Object.entries(state.endpoints)
    .map(([endpoint, stat]) => ({
      endpoint,
      llamadas: stat.count,
      errores: stat.errors,
      canceladas: stat.canceled ?? 0,
      "ms medio": meanMs(stat),
    }))
    .sort((a, b) => b.llamadas - a.llamadas);

const api = {
  rows,
  state: () => state,
  total: () => state.totalRequests,
  table: () => {
    // eslint-disable-next-line no-console
    console.table(rows());
    // eslint-disable-next-line no-console
    console.log(
      `${state.totalRequests} peticiones desde ${state.startedAt} · ` +
        `${Object.keys(state.endpoints).length} endpoints distintos · ` +
        `${Object.keys(state.screens).length} pantallas`
    );
  },
  reset: () => {
    state = emptyState();
    dirty = true;
    flush();
  },
  dump: () => {
    flush();
    const blob = new Blob(
      [
        JSON.stringify(
          { ...state, endpoints: endpointsWithMean(state.endpoints) },
          null,
          2
        ),
      ],
      { type: "application/json" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `innovus-requests-${state.startedAt.slice(0, 19).replace(/[:T]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export type RequestCounter = typeof api;

/**
 * Attaches the counter to an axios instance. Instances created with
 * axios.create() do NOT inherit interceptors, so every instance that carries
 * real traffic has to be passed in explicitly.
 */
export const installRequestCounter = (...instances: AxiosInstance[]) => {
  if (!ENABLED) return;

  const startedAt = new WeakMap<object, number>();

  instances.forEach((instance) => {
    instance.interceptors.request.use((config) => {
      try {
        startedAt.set(config, performance.now());
      } catch {
        // Instrumentation must never break a request.
      }
      return config;
    });

    instance.interceptors.response.use(
      (response: AxiosResponse) => {
        try {
          const began = startedAt.get(response.config);
          record(
            response.config.method ?? "get",
            response.config.url,
            began === undefined ? 0 : performance.now() - began,
            false
          );
        } catch {
          // Ignore: never turn a successful response into a failure.
        }
        return response;
      },
      (error: AxiosError) => {
        try {
          if (error.config) {
            const began = startedAt.get(error.config);
            // An aborted request is not a failure of the endpoint: it is the
            // dead traffic T3 exists to remove, so it gets its own column.
            const canceled = error.code === "ERR_CANCELED";
            record(
              error.config.method ?? "get",
              error.config.url,
              began === undefined ? 0 : performance.now() - began,
              !canceled,
              canceled
            );
          }
        } catch {
          // Ignore: the original rejection is what matters.
        }
        return Promise.reject(error);
      }
    );
  });

  window.setInterval(flush, FLUSH_INTERVAL_MS);
  window.addEventListener("beforeunload", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });

  (window as unknown as Record<string, unknown>).__innovusRequests = api;

  // eslint-disable-next-line no-console
  console.info(
    "[requestCounter] activo. __innovusRequests.table() para ver el resumen, " +
      ".dump() para descargar el JSON, .reset() para empezar de cero."
  );
};
